import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrandingContext } from '../contexts/BrandingContext';
import { AppLayout } from '../components/AppLayout';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectService } from '../services/projectService';
import { SettingsService } from '../services/settingsService';
import { Project, PDFTemplate } from '../types';
import { calculateProjectTotals, formatCurrency } from '../utils/calculations';
import { Asset, Moment } from '../types';
import { PDFExporter } from '../utils/pdfExport';
import { ImageGallery } from '../components/ImageGallery';
import { Icon } from '../components/Icon';
import '../styles/QuoteDetailPage.css';

export const QuoteDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useBrandingContext();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfTemplates, setPdfTemplates] = useState<PDFTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PDFTemplate | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [exportMode, setExportMode] = useState<'print' | 'pdf' | null>(null);

  useEffect(() => {
    loadProject();
    loadPDFTemplates();
  }, [projectId]);

  const loadProject = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!projectId) {
        setError('No project ID provided');
        return;
      }

      const data = await ProjectService.getProject(projectId);
      if (!data) {
        setError('Quote not found');
        return;
      }

      setProject(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPDFTemplates = async () => {
    try {
      const settings = await SettingsService.getSettings();
      let templates = settings.quotes.pdfTemplates || [];

      // If no templates exist, create default ones
      if (templates.length === 0) {
        templates = [
          {
            id: 'template_internal',
            name: 'Internal View',
            description: 'Full details for internal use',
            visibility: {
              showMaterials: true,
              showProcesses: true,
              showMaterialBreakdown: true,
              showMarkupDetails: true,
              showProcessCosts: true,
              showItemTotals: true,
              showSectionTotals: true,
              showUnitCosts: true,
            },
            isDefault: true,
            createdAt: new Date(),
          },
          {
            id: 'template_client',
            name: 'Client View',
            description: 'For client distribution',
            visibility: {
              showMaterials: true,
              showProcesses: false,
              showMaterialBreakdown: false,
              showMarkupDetails: false,
              showProcessCosts: false,
              showItemTotals: false,
              showSectionTotals: true,
              showUnitCosts: false,
            },
            isDefault: false,
            createdAt: new Date(),
          },
        ];

        // Save the default templates to settings
        const updatedSettings = {
          ...settings,
          quotes: {
            ...settings.quotes,
            pdfTemplates: templates,
          },
        };
        await SettingsService.updateSettings(updatedSettings);
      }

      setPdfTemplates(templates);

      // Set default template if available
      const defaultTemplate = templates.find((t) => t.isDefault);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
      } else if (templates.length > 0) {
        setSelectedTemplate(templates[0]);
      }
    } catch (err) {
      console.error('Failed to load PDF templates:', err);
    }
  };

  const calculateAssetTotal = (asset: Asset): number => {
    return (asset.materials || []).reduce((sum, material) => sum + material.totalCostAfterMarkup, 0);
  };

  const calculateMomentTotal = (moment: Moment): number => {
    return (moment.assets || []).reduce((sum, asset) => sum + calculateAssetTotal(asset), 0);
  };

  const calculateSubtotal = () => {
    if (!project) return 0;
    let subtotal = 0;
    project.moments.forEach((moment) => {
      subtotal += calculateMomentTotal(moment);
    });
    return subtotal;
  };

  const calculateTaxableSubtotal = (): number => {
    if (!project) return 0;
    let taxableSubtotal = 0;
    project.moments.forEach((moment) => {
      (moment.assets || []).forEach((asset) => {
        // Only include taxable assets (default to taxable if isTaxable is not set)
        if (asset.isTaxable !== false) {
          taxableSubtotal += calculateAssetTotal(asset);
        }
      });
    });
    return taxableSubtotal;
  };

  const subtotal = calculateSubtotal();
  const projectDiscount = project?.projectLevelDiscount || 0;
  const shippingCost = project?.shippingCost || 0;
  const taxRate = project?.taxRate || 0;

  // Calculate base totals with full subtotal
  const baseProjectTotals = calculateProjectTotals(subtotal, projectDiscount, shippingCost, 0);

  // Calculate tax only on taxable subtotal
  const taxableSubtotal = calculateTaxableSubtotal();
  const taxableBeforeTax = (taxableSubtotal - (taxableSubtotal * (projectDiscount / 100))) + shippingCost;
  const customTaxAmount = taxableBeforeTax > 0 ? taxableBeforeTax * (taxRate / 100) : 0;

  const totals = {
    ...baseProjectTotals,
    taxAmount: customTaxAmount,
    finalTotal: baseProjectTotals.subtotalBeforeTax + customTaxAmount,
  };

  // Check if user has permission to export/print
  const canExportPDF = () => {
    if (!user || !settings?.roles) return false;
    const userRole = user.role as 'admin' | 'sales' | 'viewer';
    return settings.roles[userRole]?.canExportPDF ?? true;
  };

  const handlePrint = () => {
    console.log('handlePrint called');
    console.log('canExportPDF:', canExportPDF());
    console.log('pdfTemplates:', pdfTemplates);

    if (!canExportPDF()) {
      alert('You do not have permission to print quotes');
      return;
    }

    console.log('Setting exportMode to print and showing template selector');
    setExportMode('print');
    setShowTemplateSelector(true);
  };

  const handleExportPDF = async () => {
    if (!canExportPDF()) {
      alert('You do not have permission to export quotes');
      return;
    }

    setExportMode('pdf');
    setShowTemplateSelector(true);
  };

  const applyTemplateVisibility = (template?: PDFTemplate) => {
    const printContent = document.getElementById('quote-detail-print-content');
    if (!printContent || !template?.visibility) return;

    const visibility = template.visibility;

    // Hide materials entire section if showMaterials is false
    if (!visibility.showMaterials) {
      const materialTables = printContent.querySelectorAll('[data-pdf-materials="true"]');
      materialTables.forEach((table) => {
        const parentDiv = (table as HTMLElement).closest('.asset-detail');
        if (parentDiv) {
          (parentDiv as HTMLElement).style.display = 'none';
        }
      });
    }

    // Apply visibility rules
    const visibilityRules = [
      { selector: '[data-pdf-material-breakdown]', key: 'showMaterialBreakdown' },
      { selector: '[data-pdf-markup]', key: 'showMarkupDetails' },
      { selector: '[data-pdf-process-costs]', key: 'showProcessCosts' },
      { selector: '[data-pdf-item-totals]', key: 'showItemTotals' },
      { selector: '[data-pdf-section-totals]', key: 'showSectionTotals' },
      { selector: '[data-pdf-unit-costs]', key: 'showUnitCosts' },
      { selector: '[data-pdf-processes]', key: 'showProcesses' },
    ] as const;

    visibilityRules.forEach(({ selector, key }) => {
      const elements = printContent.querySelectorAll(selector);
      const isVisible = visibility[key];

      elements.forEach((el) => {
        if (!isVisible) {
          (el as HTMLElement).style.display = 'none';
        } else {
          (el as HTMLElement).style.display = '';
        }
      });
    });
  };

  const resetTemplateVisibility = () => {
    const printContent = document.getElementById('quote-detail-print-content');
    if (!printContent) return;

    // Reset all visibility by removing inline styles
    const allElements = printContent.querySelectorAll('[style*="display"]');
    allElements.forEach((el) => {
      (el as HTMLElement).style.display = '';
    });
  };

  const exportPDFWithTemplate = async (template?: PDFTemplate) => {
    console.log('exportPDFWithTemplate called');
    console.log('template:', template);
    console.log('exportMode:', exportMode);

    try {
      const filename = `${project?.name || 'quote'}.pdf`;

      if (exportMode === 'print') {
        // For print mode, use iframe method for better style support
        setShowTemplateSelector(false);
        setExportMode(null);

        console.log('Starting print process...');

        setTimeout(() => {
          // Get the print content
          const printContent = document.getElementById('quote-detail-print-content');
          if (!printContent) {
            alert('Print content not found');
            console.error('Print content element not found');
            return;
          }

          console.log('Print content found, applying template filters...');

          // Apply template visibility directly to the actual element temporarily
          const elementsToRestore: Array<{element: HTMLElement, originalDisplay: string}> = [];

          if (template?.visibility) {
            const visibility = template.visibility;

            // Hide materials entire section if showMaterials is false
            if (!visibility.showMaterials) {
              const materialTables = printContent.querySelectorAll('[data-pdf-materials="true"]');
              materialTables.forEach((table) => {
                const parentDiv = (table as HTMLElement).closest('.asset-detail');
                if (parentDiv) {
                  const el = parentDiv as HTMLElement;
                  elementsToRestore.push({element: el, originalDisplay: el.style.display});
                  el.style.display = 'none';
                }
              });
            }

            // Apply visibility rules
            const visibilityRules = [
              { selector: '[data-pdf-material-breakdown]', key: 'showMaterialBreakdown' },
              { selector: '[data-pdf-markup]', key: 'showMarkupDetails' },
              { selector: '[data-pdf-process-costs]', key: 'showProcessCosts' },
              { selector: '[data-pdf-item-totals]', key: 'showItemTotals' },
              { selector: '[data-pdf-section-totals]', key: 'showSectionTotals' },
              { selector: '[data-pdf-unit-costs]', key: 'showUnitCosts' },
              { selector: '[data-pdf-processes]', key: 'showProcesses' },
            ] as const;

            visibilityRules.forEach(({ selector, key }) => {
              const elements = printContent.querySelectorAll(selector);
              const isVisible = visibility[key];

              elements.forEach((el) => {
                if (!isVisible) {
                  const element = el as HTMLElement;
                  elementsToRestore.push({element, originalDisplay: element.style.display});
                  element.style.display = 'none';
                }
              });
            });
          }

          console.log('Triggering print with CSS @media print handling visibility...');

          // Trigger print immediately - CSS @media print rules will handle showing only the print view
          window.print();

          // Restore filtered elements after print dialog closes
          const afterPrint = () => {
            console.log('Restoring filtered elements after print...');

            elementsToRestore.forEach(({element, originalDisplay}) => {
              element.style.display = originalDisplay;
            });

            window.removeEventListener('afterprint', afterPrint);
          };

          window.addEventListener('afterprint', afterPrint);
        }, 100);
      } else {
        // For export mode, export the PDF from the print view with template visibility settings
        setShowTemplateSelector(false);
        setExportMode(null);

        try {
          await PDFExporter.exportElementToPDF('quote-detail-print-content', {
            filename,
            title: `Quote - ${project?.name}`,
            orientation: 'portrait',
            templateVisibility: template?.visibility,
          });
          console.log('PDF export completed successfully');
        } catch (err) {
          console.error('PDF export error:', err);
          alert('Failed to export PDF. Please try again.');
        }
      }
    } catch (error) {
      alert('Failed to process PDF. Please try again.');
      console.error(error);
      setShowTemplateSelector(false);
    }
  };

  const handleEdit = () => {
    navigate(`/quotes/${projectId}/edit`);
  };

  if (loading) {
    return (
      <div className="quote-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="quote-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Quote not found'}</p>
          <button onClick={() => navigate('/quotes')} className="btn btn-primary">
            Back to Quotes
          </button>
        </div>
      </div>
    );
  }

  // Render the print view content (used for both printing and PDF export)
  // Always hidden off-screen - only used when cloning for new tab or PDF
  const renderPrintView = () => (
    <div id="quote-detail-print-content" className="detail-container print-view" style={{ position: 'absolute', left: '-9999px', top: '0' }}>
          <main className="detail-main">
            {/* PDF Header */}
            <div className="pdf-header">
              <div className="pdf-header-left">
                <h1>QUOTE</h1>
                <p>Date: {new Date().toLocaleDateString()}</p>
                <p>Quote ID: {projectId?.slice(0, 8)}</p>
              </div>
              <div className="pdf-header-right">
                {settings?.branding?.logoUrl && (
                  <img src={settings.branding.logoUrl} alt="Company Logo" className="company-logo" />
                )}
                <div className="company-name">{settings?.branding?.companyName || 'RedMax Events'}</div>
              </div>
            </div>

            {/* Quote Info - Compact */}
            <section className="detail-section" style={{ paddingBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '20px' }}>{project.name}</h2>
                  <p style={{ margin: '0', fontSize: '13px', color: 'var(--secondary-text)' }}>
                    Client: {project.clientName}
                  </p>
                </div>
                <span className={`status-badge status-${project.status}`}>
                  {project.status.toUpperCase()}
                </span>
              </div>

              {project.description && (
                <div style={{ fontSize: '13px', color: 'var(--secondary-text)', marginTop: '8px' }}>
                  {project.description}
                </div>
              )}
            </section>

            {/* Render all moments and content here - copy from below */}
            {project.moments.length === 0 ? (
              <div className="empty-state">
                <p>No items in this quote</p>
              </div>
            ) : (
              <div className="moments-detail">
                {project.moments.map((moment) => (
                  <div key={moment.id} className="moment-detail">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 className="moment-title">{moment.name}</h3>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--primary-button)' }}>
                        {formatCurrency(calculateMomentTotal(moment))}
                      </span>
                    </div>

                    {/* Moment Photos */}
                    {moment.photos && moment.photos.length > 0 && (
                      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
                        <ImageGallery images={moment.photos} size="medium" />
                      </div>
                    )}

                    {(moment.assets || []).length === 0 ? (
                      <p className="empty-section">No items in this section</p>
                    ) : (
                      <div className="assets-detail">
                        {moment.assets.map((asset) => (
                          <div key={asset.id} className="asset-detail">
                            <h4>{asset.name}</h4>

                            {/* Asset Photos */}
                            {asset.photos && asset.photos.length > 0 && (
                              <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                                <ImageGallery images={asset.photos} size="small" />
                              </div>
                            )}

                            {(asset.materials || []).length === 0 ? (
                              <p className="empty-item">No materials</p>
                            ) : (
                              <table className="materials-table" data-pdf-materials="true">
                                <thead>
                                  <tr>
                                    <th>Material</th>
                                    <th>Qty</th>
                                    <th data-pdf-unit-costs>Unit Price</th>
                                    <th data-pdf-processes>Processes</th>
                                    <th data-pdf-item-totals>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {asset.materials.map((material) => (
                                    <React.Fragment key={material.id}>
                                      <tr>
                                        <td>
                                          <strong>{material.materialName}</strong>
                                        </td>
                                        <td>{material.quantity}</td>
                                        <td className="price-cell" data-pdf-unit-costs>
                                          <div>
                                            <span className="before" data-pdf-markup>Cost: {formatCurrency(material.calculatedBreakdown.materialCost)}</span>
                                            <br data-pdf-markup />
                                            <span className="after">Sale: {formatCurrency(material.calculatedBreakdown.materialAfterMarkup)}</span>
                                          </div>
                                        </td>
                                        <td className="processes-cell" data-pdf-processes>
                                          {material.modifiers.some((m) => m.isSelected) ? (
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                              {material.modifiers
                                                .filter((m) => m.isSelected)
                                                .map((modifier) => (
                                                  <div key={modifier.processId} style={{ fontSize: '13px', paddingBottom: '4px' }}>
                                                    {modifier.processName}: <span data-pdf-process-costs>{formatCurrency(modifier.costValue)}</span>
                                                  </div>
                                                ))}
                                              <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '6px', paddingTop: '6px', fontWeight: '600', fontSize: '13px' }}>
                                                Total: {formatCurrency(material.calculatedBreakdown.processCosts.reduce((sum, cost) => sum + cost, 0))}
                                              </div>
                                            </div>
                                          ) : (
                                            <span>—</span>
                                          )}
                                        </td>
                                        <td className="total-cell" data-pdf-item-totals>
                                          <strong>{formatCurrency(material.totalCostAfterMarkup)}</strong>
                                        </td>
                                      </tr>
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Pricing Summary */}
            <section className="detail-section pricing-section" data-pdf-section-totals>
              <h3>Pricing Summary</h3>
              <div className="pricing-table">
                <div className="pricing-row">
                  <span className="label">Subtotal</span>
                  <span className="value">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.discountAmount > 0 && (
                  <div className="pricing-row discount">
                    <span className="label">Discount ({project.projectLevelDiscount}%)</span>
                    <span className="value">-{formatCurrency(totals.discountAmount)}</span>
                  </div>
                )}
                {totals.shippingCost > 0 && (
                  <div className="pricing-row">
                    <span className="label">Shipping</span>
                    <span className="value">{formatCurrency(totals.shippingCost)}</span>
                  </div>
                )}
                <div className="pricing-row">
                  <span className="label">Subtotal Before Tax</span>
                  <span className="value">{formatCurrency(totals.subtotalBeforeTax)}</span>
                </div>
                {totals.taxAmount > 0 && (
                  <div className="pricing-row">
                    <span className="label">Tax ({project.taxRate}%)</span>
                    <span className="value">{formatCurrency(totals.taxAmount)}</span>
                  </div>
                )}
                <div className="pricing-row final">
                  <span className="label">Total</span>
                  <span className="value">{formatCurrency(totals.finalTotal)}</span>
                </div>
              </div>
            </section>
          </main>
        </div>
  );

  return (
    <AppLayout>
      <div className="quote-detail-page">
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Quotes', path: '/quotes' },
              { label: project.name },
            ]}
          />
        </div>
        <div className="header-content">
          <div>
            <h1>{project.name}</h1>
            <p className="client-name">Client: {project.clientName}</p>
          </div>
          <div className="header-actions">
            <button onClick={handleEdit} className="btn btn-primary">
              Edit
            </button>
            <button onClick={handlePrint} className="btn btn-secondary">
              Print
            </button>
            <button onClick={handleExportPDF} className="btn btn-secondary">
              Export PDF
            </button>
            <button onClick={() => navigate('/quotes')} className="btn btn-secondary">
              Back
            </button>
          </div>
        </div>

      <div id="quote-detail-content" className="detail-container">
        <main className="detail-main">
          {/* Quote Info */}
          <section className="detail-section">
            <div className="section-header">
              <h2>Quote Information</h2>
              <span className={`status-badge status-${project.status}`}>
                {project.status.toUpperCase()}
              </span>
            </div>

            <div className="info-grid">
              <div className="info-item">
                <span className="label">Project Name</span>
                <span className="value">{project.name}</span>
              </div>
              <div className="info-item">
                <span className="label">Client</span>
                <span className="value">{project.clientName}</span>
              </div>
              <div className="info-item">
                <span className="label">Created</span>
                <span className="value">
                  {new Date(project.createdAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Last Updated</span>
                <span className="value">
                  {new Date(project.updatedAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                </span>
              </div>
              {project.expirationDate && (
                <div className="info-item">
                  <span className="label">Expiration Date</span>
                  <span
                    className="value"
                    style={{
                      color: project.isExpired ? '#dc2626' : 'inherit',
                      fontWeight: project.isExpired ? 600 : 'normal',
                    }}
                  >
                    {new Date(project.expirationDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    {project.isExpired && ' (Expired)'}
                  </span>
                </div>
              )}
            </div>

            {/* Paint Schedule Section */}
            {(() => {
              // Collect all paint dates from assets
              const paintSchedule: Array<{
                assetName: string;
                momentName: string;
                type: 'Internal Paint' | 'External Paint Shop';
                date: Date;
                label: string;
              }> = [];

              project.moments?.forEach((moment: Moment) => {
                moment.assets?.forEach((asset: Asset) => {
                  if (asset.internalPaint && asset.internalPaintDate) {
                    try {
                      const date = asset.internalPaintDate instanceof Date
                        ? asset.internalPaintDate
                        : (typeof (asset.internalPaintDate as any).toDate === 'function'
                          ? (asset.internalPaintDate as any).toDate()
                          : new Date(asset.internalPaintDate as any));
                      paintSchedule.push({
                        assetName: asset.name,
                        momentName: moment.name,
                        type: 'Internal Paint',
                        date: date,
                        label: 'Paint Date',
                      });
                    } catch (e) {
                      console.error('Error parsing internal paint date:', e);
                    }
                  }
                  if (asset.externalPaintShop && asset.externalPaintDropOffDate) {
                    try {
                      const date = asset.externalPaintDropOffDate instanceof Date
                        ? asset.externalPaintDropOffDate
                        : (typeof (asset.externalPaintDropOffDate as any).toDate === 'function'
                          ? (asset.externalPaintDropOffDate as any).toDate()
                          : new Date(asset.externalPaintDropOffDate as any));
                      paintSchedule.push({
                        assetName: asset.name,
                        momentName: moment.name,
                        type: 'External Paint Shop',
                        date: date,
                        label: 'Drop-off Date',
                      });
                    } catch (e) {
                      console.error('Error parsing external paint date:', e);
                    }
                  }
                });
              });

              // Sort by date
              paintSchedule.sort((a, b) => a.date.getTime() - b.date.getTime());

              if (paintSchedule.length === 0) return null;

              return (
                <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>
                    Paint Schedule
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {paintSchedule.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '12px',
                          backgroundColor: 'var(--bg-secondary)',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', color: 'var(--text-primary)' }}>
                            {item.assetName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--secondary-text)', marginBottom: '2px' }}>
                            Section: {item.momentName}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                            {item.type}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '12px', color: 'var(--secondary-text)', marginBottom: '4px' }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                            {item.date.toLocaleDateString('en-US', { timeZone: 'UTC' })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Project Timeline */}
            {(() => {
              // DEBUG: Log project data
              console.log('=== TIMELINE DEBUG ===');
              console.log('Full project object:', project);
              console.log('Event Date:', project.eventDate);
              console.log('Install Start Date:', project.installStartDate);
              console.log('Install End Date:', project.installEndDate);
              console.log('Revision Deadline:', project.revisionDeadline);
              console.log('Expiration Date:', project.expirationDate);

              const now = new Date();
              const milestones: Array<{
                date: Date;
                label: string;
                icon: string;
                color: string;
                isPast: boolean;
              }> = [];

              // Add created date
              milestones.push({
                date: new Date(project.createdAt),
                label: 'Quote Created',
                icon: 'document',
                color: '#667eea',
                isPast: true,
              });

              // Add event date if exists
              if (project.eventDate) {
                milestones.push({
                  date: new Date(project.eventDate),
                  label: 'Event Date',
                  icon: 'star',
                  color: '#10b981',
                  isPast: new Date(project.eventDate) < now,
                });
              }

              // Add install start if exists
              if (project.installStartDate) {
                milestones.push({
                  date: new Date(project.installStartDate),
                  label: 'Install Start',
                  icon: 'truck',
                  color: '#f59e0b',
                  isPast: new Date(project.installStartDate) < now,
                });
              }

              // Add install end if exists
              if (project.installEndDate) {
                milestones.push({
                  date: new Date(project.installEndDate),
                  label: 'Install End',
                  icon: 'truck-return',
                  color: '#f59e0b',
                  isPast: new Date(project.installEndDate) < now,
                });
              }

              // Add revision deadline if exists
              if (project.revisionDeadline) {
                milestones.push({
                  date: new Date(project.revisionDeadline),
                  label: 'Revision Deadline',
                  icon: 'edit',
                  color: '#8b5cf6',
                  isPast: new Date(project.revisionDeadline) < now,
                });
              }

              // Add expiration if exists
              if (project.expirationDate) {
                milestones.push({
                  date: new Date(project.expirationDate),
                  label: 'Quote Expires',
                  icon: 'alert',
                  color: project.isExpired ? '#dc2626' : '#6b7280',
                  isPast: new Date(project.expirationDate) < now,
                });
              }

              // Sort milestones by date
              milestones.sort((a, b) => a.date.getTime() - b.date.getTime());

              // DEBUG: Log milestones array
              console.log('Milestones array:', milestones);
              console.log('Milestones count:', milestones.length);

              // Calculate progress percentage
              if (milestones.length < 2) {
                console.log('Timeline not showing: less than 2 milestones');
                return null;
              }

              const startTime = milestones[0].date.getTime();
              const endTime = milestones[milestones.length - 1].date.getTime();
              const currentTime = now.getTime();
              const progressPercent = Math.min(
                100,
                Math.max(0, ((currentTime - startTime) / (endTime - startTime)) * 100)
              );

              return (
                <div className="timeline-container">
                  <h4 style={{ marginBottom: '24px', fontSize: '16px', fontWeight: '600', color: 'var(--main-text)' }}>
                    Project Timeline
                  </h4>
                  <div className="timeline">
                    <div className="timeline-line">
                      <div
                        className="timeline-progress"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="timeline-milestones">
                      {milestones.map((milestone, index) => (
                        <div
                          key={index}
                          className="timeline-milestone"
                          style={{
                            left: index === 0 ? '0%' : index === milestones.length - 1 ? '100%' : `${(index / (milestones.length - 1)) * 100}%`
                          }}
                        >
                          <div
                            className={`milestone-icon ${milestone.isPast ? 'past' : 'future'}`}
                            style={{
                              backgroundColor: milestone.isPast ? milestone.color : 'var(--card-background)',
                              borderColor: milestone.color,
                            }}
                          >
                            <Icon
                              name={milestone.icon}
                              size={16}
                              color={milestone.isPast ? 'white' : milestone.color}
                            />
                          </div>
                          <div className="milestone-label">
                            <div className="milestone-title">{milestone.label}</div>
                            <div className="milestone-date">
                              {milestone.date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Current position indicator */}
                      <div
                        className="timeline-current-indicator"
                        style={{ left: `${progressPercent}%` }}
                      >
                        <div className="current-marker" />
                        <div className="current-label">Today</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {project.description && (
              <div className="description-box">
                <h4>Description</h4>
                <p>{project.description}</p>
              </div>
            )}
          </section>

          {/* Items */}
          <section className="detail-section">
            <h2>Quote Items</h2>

            {project.moments.length === 0 ? (
              <div className="empty-state">
                <p>No items in this quote</p>
              </div>
            ) : (
              <div className="moments-detail">
                {project.moments.map((moment) => (
                  <div key={moment.id} className="moment-detail">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 className="moment-title">{moment.name}</h3>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--primary-button)' }}>
                        {formatCurrency(calculateMomentTotal(moment))}
                      </span>
                    </div>

                    {/* Moment Photos */}
                    {moment.photos && moment.photos.length > 0 && (
                      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
                        <ImageGallery images={moment.photos} size="medium" />
                      </div>
                    )}

                    {(moment.assets || []).length === 0 ? (
                      <p className="empty-section">No items in this section</p>
                    ) : (
                      <div className="assets-detail">
                        {moment.assets.map((asset) => (
                          <div key={asset.id} className="asset-detail">
                            <h4>{asset.name}</h4>

                            {/* Asset Photos */}
                            {asset.photos && asset.photos.length > 0 && (
                              <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                                <ImageGallery images={asset.photos} size="small" />
                              </div>
                            )}

                            {(asset.materials || []).length === 0 ? (
                              <p className="empty-item">No materials</p>
                            ) : (
                              <table className="materials-table" data-pdf-materials="true">
                                <thead>
                                  <tr>
                                    <th>Material</th>
                                    <th>Qty</th>
                                    <th data-pdf-unit-costs="true">Unit Price</th>
                                    <th data-pdf-processes="true">Processes</th>
                                    <th data-pdf-item-totals="true">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {asset.materials.map((material) => (
                                    <React.Fragment key={material.id}>
                                      <tr>
                                        <td className="material-name" data-pdf-materials="true">
                                          <strong>{material.materialName}</strong>
                                        </td>
                                        <td className="qty-cell" data-pdf-materials="true">{material.quantity}</td>
                                        <td className="price-cell" data-pdf-unit-costs="true">
                                          <div className="price-detail">
                                            <span className="before" data-pdf-markup="true">
                                              Cost: {formatCurrency(material.calculatedBreakdown.materialCost)}
                                            </span>
                                            <span className="after" data-pdf-markup="true">
                                              Sale: {formatCurrency(material.calculatedBreakdown.materialAfterMarkup)}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="processes-cell" data-pdf-processes="true" data-pdf-process-costs="true">
                                          {material.modifiers.filter((mod) => mod.isSelected).length === 0 ? (
                                            <span style={{ color: 'var(--border-color)' }}>—</span>
                                          ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                              {material.modifiers
                                                .filter((mod) => mod.isSelected)
                                                .map((mod) => (
                                                  <div key={mod.processId} style={{ fontSize: '11px', color: 'var(--secondary-text)' }}>
                                                    {mod.processName}: <span data-pdf-process-costs="true">{formatCurrency(mod.costValue)}</span>
                                                  </div>
                                                ))}
                                              <div style={{ fontSize: '12px', color: 'var(--main-text)', fontWeight: '600', marginTop: '4px' }}>
                                                Total: {formatCurrency(material.calculatedBreakdown.processCosts.reduce((sum, cost) => sum + cost, 0))}
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                        <td className="total-cell" data-pdf-item-totals="true">
                                          <strong>
                                            {formatCurrency(material.totalCostAfterMarkup)}
                                          </strong>
                                        </td>
                                      </tr>
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* Sidebar - Totals */}
        <aside className="detail-sidebar" data-pdf-section-totals="true">
          <div className="totals-summary">
            <h3>Quote Summary</h3>

            <div className="summary-row">
              <span>Subtotal:</span>
              <span>{formatCurrency(totals.subtotal)}</span>
            </div>

            {project.projectLevelDiscount > 0 && (
              <>
                <div className="summary-row discount">
                  <span>Discount ({project.projectLevelDiscount}%):</span>
                  <span>-{formatCurrency(totals.discountAmount)}</span>
                </div>
                <div className="summary-row">
                  <span>After Discount:</span>
                  <span>{formatCurrency(totals.subtotalAfterDiscount)}</span>
                </div>
              </>
            )}

            <div className="summary-row">
              <span>Shipping:</span>
              <span>{formatCurrency(project.shippingCost || 0)}</span>
            </div>

            <div className="summary-row">
              <span>Tax ({project.taxRate || 0}%):</span>
              <span>{formatCurrency(totals.taxAmount || 0)}</span>
            </div>

            <div className="summary-row final">
              <span>TOTAL:</span>
              <strong>{formatCurrency(totals.finalTotal)}</strong>
            </div>

            <div className="summary-actions">
              <button onClick={handleEdit} className="btn btn-primary btn-block">
                Edit Quote
              </button>
              <button onClick={handleExportPDF} className="btn btn-secondary btn-block">
                Export as PDF
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--card-background)',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <h2 style={{ margin: '0 0 16px 0', color: 'var(--main-text)' }}>
              Select Export Template
            </h2>
            <p style={{ margin: '0 0 20px 0', color: 'var(--secondary-text)', fontSize: '14px' }}>
              Choose a template to customize what appears in the exported PDF
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {pdfTemplates.length > 0 ? (
                pdfTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => exportPDFWithTemplate(template)}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      background: selectedTemplate?.id === template.id ? 'var(--primary-button)' : 'var(--card-background)',
                      color: selectedTemplate?.id === template.id ? 'var(--primary-button-text)' : 'var(--main-text)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ fontWeight: '600' }}>{template.name}</div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: selectedTemplate?.id === template.id ? 'rgba(255, 255, 255, 0.8)' : 'var(--secondary-text)',
                        marginTop: '4px',
                      }}
                    >
                      {template.description}
                    </div>
                  </button>
                ))
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--secondary-text)' }}>
                  Loading templates...
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowTemplateSelector(false);
                setExportMode(null);
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                background: 'var(--main-background)',
                color: 'var(--main-text)',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Hidden print view - always rendered for PDF export */}
      {renderPrintView()}
      </div>
    </AppLayout>
  );
};
